import { useRef, useState } from 'react'
import { Upload } from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import { Spinner } from '../../lib/shadcn/spinner'
import { toast } from '../../lib/shadcn/sonner'
import { useMarketingUpload } from '../../hooks/useMarketingUpload'

/**
 * Uploads a file to the private marketing-content bucket, then records it.
 *
 * The three steps and the reasoning behind them live in hooks/useMarketingUpload,
 * because the design request dialog needs the same sequence and cannot use this
 * button: it has no record to attach to until the request has been created.
 *
 * kind is what separates a reference sent in with a request from the artwork
 * delivered against it. The database enforces the difference; this is only the
 * label on the way in. See db/migrations/0118.
 */
export function UploadButton({
  contentItemId,
  designRequestId,
  kind = 'Artwork',
  label = 'Add a file',
  onUploaded,
  disabled = false,
}: {
  contentItemId?: string
  designRequestId?: string
  kind?: 'Reference' | 'Artwork'
  label?: string
  onUploaded: () => void
  disabled?: boolean
}) {
  const { upload } = useMarketingUpload()
  const inputRef = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)

  async function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return
    const chosen = Array.from(files)

    setBusy(true)
    const { done, failed, tooBig } = await upload(chosen, {
      contentItemId: contentItemId ?? null,
      designRequestId: designRequestId ?? null,
      kind,
    })
    setBusy(false)
    if (inputRef.current) inputRef.current.value = ''

    if (tooBig.length > 0) {
      toast.error('That file is too large', {
        description: `${tooBig.join(', ')} is over 200 MB. Compress it or upload a shorter cut.`,
      })
    }
    if (failed.length > 0) {
      toast.error(
        done === 0 ? 'Nothing was uploaded' : `${done} of ${chosen.length} uploaded, ${failed.length} did not`,
        { description: failed.join(' | ') },
      )
    } else if (done > 0) {
      toast.success(done === 1 ? 'File added' : `${done} files added`)
    }
    // Called even when every file failed, because the list may still have
    // changed: an earlier file in the same batch can have landed.
    onUploaded()
  }

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => void handleFiles(e.target.files)}
      />
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="h-8 gap-1.5"
        disabled={disabled || busy}
        onClick={() => inputRef.current?.click()}
      >
        {busy ? <Spinner className="h-3.5 w-3.5" /> : <Upload className="h-3.5 w-3.5" />}
        {busy ? 'Uploading' : label}
      </Button>
    </>
  )
}
